import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuote } from "./QuoteDialog";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/lnzv4077_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2003_06_30%20PM.png";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "Printers", to: "/printers" },
  { label: "Finishing", to: "/finishing" },
  { label: "Supplies", to: "/supplies" },
  { label: "Bundles", to: "/bundles" },
  { label: "Visibility Check™", to: "/visibility-check" },
  { label: "How It Works", anchor: "#how-it-works" },
];

export default function Header() {
  const { openQuote } = useQuote();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, item) => {
    e.preventDefault();
    setOpen(false);
    if (item.to) {
      navigate(item.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (item.anchor) {
      const anchorId = item.anchor;
      if (location.pathname !== "/") {
        navigate("/" + anchorId);
      } else {
        const el = document.querySelector(anchorId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? "bg-white/95 border-b border-slate-200 shadow-sm"
          : "bg-white/80 border-b border-transparent"
      }`}
    >
      {/* CMYK brand ribbon */}
      <div className="cmyk-ribbon h-[3px] w-full" aria-hidden />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-24 md:h-28">
          <a
            href="/"
            data-testid="header-logo-link"
            aria-label="ED Tech Depot — home"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center group"
          >
            <img
              src={LOGO_URL}
              alt="ED Tech Depot — Poster, Banner, Display Solutions for Schools"
              className="h-16 md:h-20 lg:h-24 w-auto object-contain"
              loading="eager"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.to || item.anchor}
                onClick={(e) => handleNav(e, item)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className="text-sm font-semibold text-slate-700 hover:text-teal transition-colors relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-teal transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="header-request-quote-btn"
              onClick={openQuote}
              title="Opens in a new tab"
              className="hidden sm:inline-flex bg-teal hover:bg-[#0D6BB1] text-white font-bold px-5 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 text-sm"
            >
              Request a Quote
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 rounded-md text-navy hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-slate-200 py-4 space-y-1"
          >
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.to || item.anchor}
                onClick={(e) => handleNav(e, item)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className="block px-4 py-3 rounded-md text-base font-semibold text-navy hover:bg-slate-50"
              >
                {item.label}
              </a>
            ))}
            <button
              data-testid="mobile-request-quote-btn"
              onClick={() => {
                setOpen(false);
                openQuote();
              }}
              title="Opens in a new tab"
              className="w-full mt-2 bg-teal hover:bg-[#0D6BB1] text-white font-bold px-5 py-3 rounded-lg"
            >
              Request a Quote
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
